import React from 'react';

import * as SearchDAO from 'dao/SearchDAO';
import {
  areLookupSpecifiersEqual,
  getServerLookupResult,
  resolveLookupSpecifierAndReturnFinalResults,
  resolveSubQueryResults,
} from 'notebook/tables/lookup-field/LookupFieldUtil';
import LookupFieldValuesStaticTable from 'notebook/tables/lookup-field/LookupFieldValuesStaticTable';
import Label from 'ui-library/bs/Label';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  LookupFieldResult,
  LookupSpecifier,
  SampleSpecifier,
  ServerLookupResult,
} from './LookupFieldDefinitionConstants';

interface LookupFieldValuesTableProps {
  isDataTable: boolean;
  batchId: string | null;
  fileId: string | null | undefined;
  stageRunId: string | null;
  lookupSpecifier: LookupSpecifier;
  serverLookupResult: ServerLookupResult | null;

  onFieldValuesChange: (serverLookupResult: ServerLookupResult | null) => void;
}

interface State {
  fetchingValues: boolean;
}
/**
 * Table that fetches lookup field results from the server and displays them
 */
class LookupFieldValuesTable extends React.Component<LookupFieldValuesTableProps, State> {
  _isMounted = false;
  override state: State = {
    fetchingValues: false,
  };

  override componentDidMount(): void {
    this._isMounted = true;
    this._fetchLookupResults(this.props);
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  override UNSAFE_componentWillReceiveProps(nextProps: LookupFieldValuesTableProps): void {
    if (
      this.props.batchId !== nextProps.batchId ||
      this.props.fileId !== nextProps.fileId ||
      !areLookupSpecifiersEqual(this.props.lookupSpecifier, nextProps.lookupSpecifier)
    ) {
      this._fetchLookupResults(nextProps);
    }
  }

  _getSampleSpecifier(): SampleSpecifier {
    const {batchId, fileId, stageRunId} = this.props;
    return {
      batchId,
      fileId,
      stageRunId,
      stageRunSampleId: null,
    };
  }

  _getTopLevelResults(): Array<LookupFieldResult> | null {
    const {serverLookupResult, lookupSpecifier} = this.props;
    if (serverLookupResult == null) {
      return null;
    }
    const resolvedServerResults = resolveSubQueryResults({serverLookupResult});
    return resolveLookupSpecifierAndReturnFinalResults({
      lookupSpecifier,
      topLevelSampleSpecifier: this._getSampleSpecifier(),
      resolvedServerResults,
    });
  }

  async _fetchLookupResults(props: LookupFieldValuesTableProps): Promise<void> {
    const {batchId, fileId, stageRunId, lookupSpecifier} = props;

    this.setState({fetchingValues: true});
    if (batchId != null || fileId != null || stageRunId != null) {
      const sampleSpecifier = this._getSampleSpecifier();
      const queries = [
        {
          ...lookupSpecifier,
          ...sampleSpecifier,
          onlyValid: true,
        },
      ];
      try {
        const response = await SearchDAO.lookupFieldValues(queries);
        const {data} = response;

        if (!areLookupSpecifiersEqual(lookupSpecifier, this.props.lookupSpecifier)) {
          // Query is stale
          return;
        }

        if (data != null) {
          const serverLookupResult = getServerLookupResult({
            serverLookupQueries: queries,
            serverLookupResults: data,
            cellLookupSpecifier: lookupSpecifier,
            rowSampleSpecifier: sampleSpecifier,
          });
          this.props.onFieldValuesChange(serverLookupResult);
        }
        if (this._isMounted) {
          this.setState({
            fetchingValues: false,
          });
        }
      } catch (e) {
        this.props.onFieldValuesChange(null);
        if (this._isMounted) {
          this.setState({
            fetchingValues: false,
          });
        }
        throw e;
      }
    }
  }

  _isLoading(): boolean {
    return (
      (this.props.batchId == null && this.props.fileId == null && this.props.stageRunId == null) ||
      this.state.fetchingValues
    );
  }

  override render(): JSX.Element {
    return (
      <div>
        <Label>VALUES</Label>
        {this._isLoading() ? (
          <div className="text-center u-spacing-top u-spacing-bottom">
            <CenteredHorizontalThrobber />
          </div>
        ) : (
          <LookupFieldValuesStaticTable
            fieldName={this.props.lookupSpecifier.fieldName || 'Value'}
            // Pass the results of the complete lookup field definition to LookupFieldValuesStaticTable
            // since we are not showing intermediate sub-queries results in the modal,
            results={this._getTopLevelResults() || []}
            isDataTable={this.props.isDataTable}
          />
        )}
      </div>
    );
  }
}

export default LookupFieldValuesTable;
